import { BardCodeType, CardActions, CardActionPayload, CardConfigDataFE, CardStatus, CardType, UserCardFE } from '../../../user.model';
import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, Output } from '@angular/core';
import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels } from '@techiediaries/ngx-qrcode';
import { animate, style, transition, trigger } from '@angular/animations';
import { Capacitor } from '@capacitor/core';
import { CardPrintService } from 'src/app/global/utils/card-print/cart-print.service';
import { ShortDatePipe } from 'src/app/global/pipes/shortDate/short-date.pipe';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../../service/user.abstract.service';
import { filter, take, tap } from 'rxjs/operators';
import { Organisation } from 'src/app/global/global.model';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { organisationConfigurationSelector, organisationSelector } from 'src/app/global/store/global.selectors';

export enum DataState {
    ENTERING = 'ENTERING',
    DONE = 'DONE',
}

export enum CardView {
    DEFAULT = 'DEFAULT',
    SUBSCRIPTIONS = 'SUBSCRIPTIONS',
}

@Component({
    selector: 'app-user-family-card',
    templateUrl: './family-card.component.html',
    styleUrls: ['./family-card.component.scss'],
    animations: [trigger('dataChange', [transition(`${DataState.DONE} => ${DataState.ENTERING}`, [style({ opacity: '0' }), animate('200ms ease', style({ opacity: '1' }))])])],
})
export class FamilyCardComponent implements OnDestroy {
    destroy$ = new Subject();

    CardStatus = CardStatus;
    CardType = CardType;
    BardCodeType = BardCodeType;
    DataState = DataState;
    CardView = CardView;
    isMobile: boolean = false;
    canShowSubscriptions = false;

    public qrcodeOpt = {
        elementType: NgxQrcodeElementTypes.URL,
        correctionLevel: NgxQrcodeErrorCorrectionLevels.HIGH,
    };

    public dataState: DataState = DataState.DONE;
    private _card: Partial<UserCardFE>;
    private _orgAndConfig: CardConfigDataFE;
    public _loading: boolean; // hack to fix ngx-qrcode rendering twice on data change between two active cards
    private _cardView: CardView = CardView.DEFAULT;
    public subscribeLoading: boolean = false;
    private allCoreConfig: any;
    public cardPrintLoading: boolean = false;
    organisation: Organisation;

    get cardView() {
        return this._cardView;
    }
    set cardView(v: CardView) {
        this._cardView = v;
    }

    @Input()
    set card(newCard: Partial<UserCardFE>) {
        this._loading = true;
        this.dataState = DataState.ENTERING;
        this.cardView = CardView.DEFAULT;
        this._card = newCard;
        this._loading = false;
    }
    get card(): Partial<UserCardFE> {
        return this._card;
    }

    @Input()
    set orgAndConfig(config: CardConfigDataFE) {
        this._orgAndConfig = config;
    }
    get orgAndConfig() {
        return this._orgAndConfig;
    }

    @Output() cardActions = new EventEmitter();

    get firstName() {
        return this.card?.firstName;
    }
    get lastName() {
        return this.card?.lastName;
    }
    get photo() {
        return this.card?.photo || 'https://www.pphfoundation.ca/wp-content/uploads/2018/05/default-avatar.png';
    }
    get status() {
        return this.card?.status;
    }
    get code() {
        return this.card?.code;
    }
    get qrCodeForImage() {
        if (this.orgName.toUpperCase() === 'BROSSARD') {
            return `QBD${this.card?.code}`;
        }
        return this.card?.code;
    }
    get cardType() {
        return this.card?.cardType;
    }
    get expDate() {
        return this.card?.expirationDate;
    }
    get orgName() {
        return this.orgAndConfig?.org?.name;
    }
    get orgLogo() {
        return this.orgAndConfig?.org?.logo;
    }
    get cardConfig() {
        return this.orgAndConfig?.config?.support?.cards?.find((c) => c.type === this.cardType);
    }
    get expDateString() {
        return this.formatDate(this.card?.expirationDate);
    }
    get displayExpDate() {
        return this.cardConfig.displayExpirationDate ?? true;
    }
    get subscriptions() {
        return this.card?.subscriptions;
    }
    get subTypeColor() {
        return this.orgAndConfig?.config?.subTypes?.find((c) => c.tag === this.card?.subType)?.color || '';
    }
    get subTypeName() {
        return this.orgAndConfig?.config?.subTypes?.find((c) => c.tag === this.card?.subType)?.name[this.translateService.currentLang] || '';
    }
    get cardLabel() {
        return this.cardConfig?.identity?.label[this.translateService.currentLang] || '';
    }
    get cardIcon() {
        return this.cardConfig?.identity?.icon || 'icon-my-card';
    }
    get linkable() {
        return this.cardConfig?.linkable;
    }
    get showCardInfo() {
        if (!this.card.constraints || typeof this.card.constraints?.displayInfo !== 'boolean') {
            return true;
        }
        return this.card.constraints?.displayInfo;
    }
    get requiredSubscription() {
        return this.card.constraints?.message || '';
    }
    public hasFamilySubscription(): boolean {
        if (this.code) {
            return true;
        } else {
            return this.subscriptions?.some((sub) => sub.subscriptionType === 'FAMILY');
        }
    }

    get showExpireSoonMessage() {
        if (this.cardType === CardType.LEGACY) {
            return false;
        }
        // Set a threshold warning date by taking expiration date - renewalDelay (ex: 60)
        // Check if today's date with .getTime() is larger than the threshold date.
        // if so, return true
        const today = new Date();
        const expirationDate = new Date(this?.card?.expirationDate);
        const warningThresholdDate = expirationDate.setDate(expirationDate.getDate() - this?.cardConfig.renewalDelay);
        return warningThresholdDate < today.getTime();
    }

    get showExpireMessage() {
        // Set a threshold warning date by taking expiration date - renewalDelay (ex: 60)
        // Check if today's date with .getTime() is larger than the threshold date.
        // if so, return true
        const today = new Date();
        const expirationDate = new Date(this?.card?.expirationDate);
        return expirationDate < today;
    }

    get instructionsGeneralIntroduction() {
        return this.cardConfig?.identity?.instructions?.general?.introduction[this.translateService.currentLang] || '';
    }

    get subscribeTooltipButton() {
        return this.cardConfig?.tooltips?.subscribe?.[this.translateService.currentLang] || '';
    }

    get linkTooltipButton() {
        return this.cardConfig?.tooltips?.link?.[this.translateService.currentLang] || '';
    }

    get canFlipCard() {
        if (this.cardType !== CardType.LEGACY) {
            return [CardStatus.ACTIVE, CardStatus.EXPIRED].includes(this.status);
        }
        return this.subscriptions.length > 0;
    }

    get legacyCardShow() {
        return this.canFlipCard;
    }

    get isLegacyCard() {
        return this.card.cardType === CardType.LEGACY;
    }

    constructor(
        private translateService: TranslateService,
        private cdRef: ChangeDetectorRef,
        private userService: UserService,
        private cardPrintService: CardPrintService,
        private shortDatePipe: ShortDatePipe,
        private store: Store<State>,
    ) {}

    ngOnInit(): void {
        this.isMobile = Capacitor.getPlatform() !== 'web';
        this.store
            .select(organisationSelector)
            .pipe(
                filter((org) => !!org),
                take(1),
                tap((org) => (this.organisation = org)),
            )
            .subscribe();

        this.store
            .select(organisationConfigurationSelector)
            .pipe(take(1))
            .subscribe((config) => {
                this.canShowSubscriptions = config?.adminTabs?.find((tab) => tab.tag === 'SUBSCRIPTION')?.visible;
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggleCardView() {
        if (this.cardView === CardView.DEFAULT) {
            this.cardView = CardView.SUBSCRIPTIONS;
        } else {
            this.cardView = CardView.DEFAULT;
        }
    }

    getToggleTooltip() {
        if (this.cardView === CardView.DEFAULT) {
            return this.translateService.instant('pages.user-cards.my-card.view-subscriptions');
        } else {
            return this.translateService.instant('pages.user-cards.my-card.view-card');
        }
    }

    getPrintTooltip() {
        return this.translateService.instant('pages.user-cards.common.button.print');
    }

    async printCardQRcode() {
        const cardPrintTemplate = this.orgAndConfig?.config?.cardPrint?.qrCode || this.allCoreConfig.defaultCardPrint.qrCode;
        const url = (
            await this.cardPrintService.generateQrCodePdf(
                this.card,
                this.organisation,
                this.orgAndConfig.config,
                this.subscriptions,
                this.translateService,
                cardPrintTemplate,
                false,
                this.shortDatePipe,
            )
        ).url;
        window.open(url.toString(), '_blank');
    }

    async printCardBarCode() {
        const cardPrintTemplate = this.orgAndConfig?.config?.cardPrint?.barCode || this.allCoreConfig.defaultCardPrint.barCode;
        const url = (
            await this.cardPrintService.generateBarCodePdf(
                this.card,
                this.organisation,
                this.orgAndConfig.config,
                this.subscriptions,
                this.translateService,
                cardPrintTemplate,
                false,
                this.shortDatePipe,
            )
        ).url;
        window.open(url as any, '_blank');
    }

    async getBase64ImageFromUrl(imageUrl: string) {
        const res = await fetch(imageUrl);
        const blob = await res.blob();

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.addEventListener(
                'load',
                () => {
                    resolve(reader.result);
                },
                false,
            );

            reader.onerror = () => {
                return reject(this);
            };
            reader.readAsDataURL(blob);
        });
    }

    printCard() {
        this.cardPrintLoading = true;
        if (this.cardConfig.barcode === BardCodeType.QRCODE) {
            this.printCardQRcode();
        }

        if (this.cardConfig.barcode === BardCodeType.BARCODE) {
            this.printCardBarCode();
        }
        this.cardPrintLoading = false;
    }

    getCardTypeClass(cardType: CardType) {
        switch (cardType) {
            case CardType.BCITI:
                return 'card-bciti';
            case CardType.LEGACY:
                return 'card-legacy';
            case CardType.CITIZEN:
                return 'card-citizen';
            case CardType.LIBRARY:
                return 'card-library';
            case CardType.SPORTS:
                return 'card-sports';
            default:
                return 'card-other';
        }
    }

    getSystExtLinkButtonClass(cardType: CardType) {
        switch (cardType) {
            case CardType.LIBRARY:
                return 'syst-ext-button-library';
            case CardType.SPORTS:
                return 'syst-ext-button-sports';
            default:
                return 'syst-ext-button-other';
        }
    }

    formatDateUTC(date: string): string {
        const options: Intl.DateTimeFormatOptions = {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC',
        };

        if (!date) {
            return '';
        }

        return new Intl.DateTimeFormat('en-CA', options).format(new Date(date));
    }

    formatDate(date: string, options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' }) {
        if (!date || this.status !== CardStatus.ACTIVE) {
            return '';
        }
        return `Exp: ${new Intl.DateTimeFormat(`${this.translateService.currentLang}-CA`, options).format(new Date(date))}`;
    }

    subscribeToCard() {
        this.cardActions.emit({ action: CardActions.SUBSCRIBE, card: this.card } as CardActionPayload);
    }

    renewCard() {
        this.cardActions.emit({ action: CardActions.RENEW, card: this.card } as CardActionPayload);
    }

    linkCard() {
        this.cardActions.emit({ action: CardActions.LINK, card: this.card } as CardActionPayload);
    }

    systExtLink() {
        this.userService
            .getSystExtLink(this.card?.cardType)
            .pipe(tap((systExtLink) => window.open(systExtLink, '_blank')))
            .subscribe();
    }
}
